import { ref, computed, watchEffect } from 'vue'
import { defineStore } from 'pinia'
import { useSessionStore } from '@/stores/SessionStore'
import { useUserStore } from '@/stores/UserStore'
import { useSpotStore } from '@/stores/SpotStore'
import { useCachedFetch } from '@/composables/useFetch'
import { useDateHelper } from '@/composables/useDateHelper'
import { useUnitConverter } from '@/composables/useUnitConverter'
import { format, isSameDay, isWithinInterval } from 'date-fns';
import { TZDate } from '@date-fns/tz'
import useStorageSystem from '@/composables/useStorageSystem'
import useConfigurationGlobals from '@/composables/useConfigurationGlobals';

const { $BASEURL } = useConfigurationGlobals();

export let useTidesStore = defineStore('tidesStore', () => {
  const sessionStore = useSessionStore();
  const userStore = useUserStore();
  const spotStore = useSpotStore();

  const dateHelper = useDateHelper();
  const unitConverter = useUnitConverter();

  const savedTideSpotTimeout = ref(null);

  const nearbyTideSpots = ref([]);
  const selectedNearbyTideSpot = ref(null);
  const tideDate = ref(new Date());
  const allData = ref([]);
  const tideSpotTimezoneOffset = ref(0);

  const [savedSelectedTideSpot, savedSelectedTideSpotLoaded] = useStorageSystem({}, 'savedSelectedTideSpot', 'apiUserStorage' )

  const dateRangeDataSetContaingSelectedDate = computed(() =>
    allData.value.find((dataSet) =>
      isWithinInterval(tideDate.value, {
        start: dataSet.startDate,
        end: dataSet.endDate
      })
    )
  )
  
  const tideData = computed(() => {
    if (!dateRangeDataSetContaingSelectedDate.value) return [];

    const dailyDataSets = dateRangeDataSetContaingSelectedDate.value.tides?.data || {}
    const selectedDateData = getDataSetForDate(dailyDataSets, tideDate.value)

    return selectedDateData || []
  })

  const tideEvents = computed(() => {
    if (!dateRangeDataSetContaingSelectedDate.value) return []

    const dailyDataSets = dateRangeDataSetContaingSelectedDate.value.tideEvents?.data || {}
    const selectedDateData = getDataSetForDate(dailyDataSets, tideDate.value)

    return selectedDateData || []
  })

  const solunarEvents = computed(() => {
    if (!dateRangeDataSetContaingSelectedDate.value) return []

    const dailyDataSets = dateRangeDataSetContaingSelectedDate.value.solunarEvents?.data || {}
    const selectedDateData = getDataSetForDate(dailyDataSets, tideDate.value)

    return selectedDateData || []
  })  

  const tideDataMin = computed(() => dateRangeDataSetContaingSelectedDate.value.tides?.minValue || null);
  const tideDataMax = computed(() => dateRangeDataSetContaingSelectedDate.value.tides?.maxValue || null);
  const hasCurrentsSpot = computed(() => nearbyTideSpots.value.some(spot => spot.type === 'currents'));
  const nextTideEvent = computed(() => tideEvents.value.find(event => event.isNext) || null);
  const nowTideValues = computed(() => {
    const nowTideValueDateTime = dateRangeDataSetContaingSelectedDate.value.tides?.nowTideValues[0];

    return isSameDay(nowTideValueDateTime, tideDate.value) ? dateRangeDataSetContaingSelectedDate.value.tides?.nowTideValues : [null, null]
  })

  const sunrise = computed(() => solunarEvents.value.find(event => event.eventName.toLowerCase().includes('sunrise')) || null);
  const sunset = computed(() => solunarEvents.value.find(event => event.eventName.toLowerCase().includes('sunset')) || null);
  const moonrise = computed(() => solunarEvents.value.find(event => event.eventName.toLowerCase().includes('moonrise')) || null);
  const moonset = computed(() => solunarEvents.value.find(event => event.eventName.toLowerCase().includes('moonset')) || null);
  const moonStatus = computed(
    () =>
      solunarEvents.value.find(
        (event) =>
          !event.eventName.toLowerCase().includes('moonset') &&
          !event.eventName.toLowerCase().includes('moonrise') &&
          !event.eventName.toLowerCase().includes('sun')
      ) || null
  );

  const tideSpotTimezone = computed(() => spotStore.localTimezone);

  async function getNearbyTideSpotsByLatLon(lat, lon) {
    const latLon = `${lat}:${lon}`;
    const distance = unitConverter.convert('mi', userStore.units.distance, 20, true)
    const data = await getSpotSetBySearch(latLon, { search_dist: distance, spot_types: '10,18' })

    if (data.error) return data;

    const processedData = transformTideSpotData(data.data_values);

    nearbyTideSpots.value = processedData;

    return data; 
  }

  async function getSpotSetBySearch(search, settings = {}, overrideCache = true) {
    const defaultSettings = {
      units_wind: userStore.units.wind,
      units_temp: userStore.units.temp,
      units_distance: userStore.units.distance,
      units_precip: userStore.units.precip,
      include_spot_products: false,
      stormprint_only: false,
      wf_token: sessionStore.token
    }

    const params = Object.assign(defaultSettings, settings)

    params.search = search

    const queryString = new URLSearchParams(params).toString()
    const url = `${$BASEURL}/spot/getSpotSetBySearch?${queryString}`

    try {
      const { error, data } = await useCachedFetch(url, {}, true, overrideCache)

      if (error.value) {
        throw error.value
      }

      if (data.value.status?.status_code !== 0 && data.value.status_code !== 0) {
        throw data.value.status || data.value.status_code
      }

      return data.value
    } catch (error) {
      console.warn(error)

      return { error: true, message: error }
    }
  }

  async function getTideData(settings, overrideCache = true) {
    const defaultSettings = {
      wf_token: sessionStore.token
    }

    const params = Object.assign(defaultSettings, settings)

    const queryString = new URLSearchParams(params).toString()
    const url = `${$BASEURL}/tide/getTideData?${queryString}`

    try {
      const { error, data } = await useCachedFetch(url, {}, true, overrideCache)

      if (error.value) {
        throw error.value
      }

      if (data.value.status?.status_code !== 0 && data.value.status_code !== 0) {
        throw data.value.status || data.value.status_code
      }

      return data.value
    } catch (error) {
      console.warn(error)

      return { error: true, message: error }
    }
  }

  async function getTideEvents(settings) {
    const defaultSettings = {
      wf_token: sessionStore.token
    }

    const params = Object.assign(defaultSettings, settings)

    const queryString = new URLSearchParams(params).toString()
    const url = `${$BASEURL}/tide/getTideEvents?${queryString}`

    try {
      const { error, data } = await useCachedFetch(url)

      if (error.value) {
        throw error.value
      }

      if (data.value.status?.status_code !== 0 && data.value.status_code !== 0) {
        throw data.value.status || data.value.status_code
      }

      return data.value
    } catch (error) {
      console.warn(error)

      return { error: true, message: error }
    }
  }

  async function getAllTideDataByDateRange(settings) {
    if (!settings.tide_site_name || !settings.begin_time || !settings.end_time) return;

    const startDate = settings.begin_time;
    const endDate = settings.end_time;
    const dateRangeCount =  settings.date_range_count || 1;

    settings.begin_time = format(new Date(settings.begin_time), 'yyyy-MM-dd HH:mm:ss');
    settings.end_time = format(new Date(settings.end_time), 'yyyy-MM-dd HH:mm:ss');

    const responseData = await Promise.allSettled([getTideData(settings), getTideEvents(settings)]);
    const repsonseDateCopy = JSON.parse(JSON.stringify(responseData));

    tideSpotTimezoneOffset.value = getTimezoneOffsetHours(repsonseDateCopy[0].value?.tidedata[0].dateTime)

    const processedTideData = processTideData(repsonseDateCopy[0].value);

    const nowTideValues = processedTideData[1];
    const minValue = processedTideData[2];
    const maxValue = processedTideData[3];
    
    const processedEventData = sortAndTransformTideEvents(repsonseDateCopy[1].value);

    const tideDataObject = {
      data: processedTideData[0],
      nowTideValues,
      minValue,
      maxValue
    }
    
    const eventDataObject = {
      data: processedEventData[0]
    }

    const solunarDataObject = {
      data: processedEventData[1]
    }

    const responseDataObject = {
      tides: tideDataObject,
      tideEvents: eventDataObject,
      solunarEvents: solunarDataObject,
      startDate,
      endDate
    }

    if (allData.value.length > dateRangeCount) allData.value = [];
    
    allData.value.push(responseDataObject);
    
    return responseDataObject
  }

  function transformTideSpotData(data) {
    return data.map((spot) => {
      const name = spot[1]
      const nameParts = name.split(', ')
      const locationName = nameParts.shift()

      return {
        id: spot[0],
        name: locationName,
        region: nameParts.join(', '),
        location: name,
        type: spot[2] === 10 ? 'tide' : 'currents',
        units: spot[2] === 10 ? 'ft' : 'kts',
        distance: spot[4],
        lat: spot[5],
        lon: spot[6]
      }
    })
  }  

  function processTideData(data) {
    const sortedAndTransformedTideData = sortAndTransformTideData(data.tidedata);
    const nowTideValues = calculateNowTideValues(sortedAndTransformedTideData[0]);

    return [sortedAndTransformedTideData[0], nowTideValues, sortedAndTransformedTideData[1], sortedAndTransformedTideData[2]]
  }

  function sortAndTransformTideData(data) {
    if (!data) return;

    const tideDataByDate = {};
    let maxValue = -Infinity;
    let minValue = Infinity;

    data.forEach(dataPoint => {
      // sort the data into days. Use ISO date string's date part as the key 2025-11-04T00:00:00-08:00 => 2025-11-04
      const dateString = dataPoint.dateTime.split('T')[0]
      const timeString = dataPoint.dateTime.split('T')[1];
      const timezoneOffsetString = timeString.indexOf('Z') > -1 ? '+00:00' : timeString.slice(8);
      const eventUTCSDate = dateHelper.convertISODateToUTC(dataPoint.dateTime)
      const eventValue = parseFloat(dataPoint.eventValue)

      if (!tideDataByDate[dateString]) {
        tideDataByDate[dateString] = []
      }

      tideDataByDate[dateString].push([eventUTCSDate, parseFloat(eventValue), timezoneOffsetString])

      // Update min/max values of entire data set
      maxValue = Math.max(maxValue, eventValue)
      minValue = Math.min(minValue, eventValue)      
    });

    return [tideDataByDate, minValue, maxValue]
  }

  function sortAndTransformTideEvents(data) {
    const solunarTerms = ['sun', 'moon', 'eclipse', 'quarter', 'solstice']

    const solunarEventsDataByDate = {};
    const tideEventsDataByDate = {};

    data.tideEvents.forEach((event) => {
      const eventDateString = event.dateTime.split('T')[0];
      const eventTimeString = event.dateTime.split('T')[1];
      const timezoneOffsetString = eventTimeString.indexOf('Z') > -1 ? '+00:00' : eventTimeString.slice(8);
      const isSolarEvent = solunarTerms.find((term) => event.eventName.toLowerCase().includes(term));

      event.dateTime = dateHelper.convertISODateToUTC(event.dateTime);
      event.timezoneOffset = timezoneOffsetString

      if (isSolarEvent) {
        if (!solunarEventsDataByDate[eventDateString]) {
          solunarEventsDataByDate[eventDateString] = [];
        }

        event.timeFormatted = format(new TZDate(event.dateTime, timezoneOffsetString), 'h:mm a')

        solunarEventsDataByDate[eventDateString].push(event);
      } else {
        if (!tideEventsDataByDate[eventDateString]) {
          tideEventsDataByDate[eventDateString] = [];
        }

        tideEventsDataByDate[eventDateString].push(event);
      }

    })

    const transformedTideEvents = transformTideEvents(tideEventsDataByDate)

    return [transformedTideEvents, solunarEventsDataByDate]
  }  

  function getTimezoneOffsetHours(date) {
    const match = date.match(/([+-])([0-9]{2}):([0-9]{2})$/)

    if (match) {
      const sign = match[1] === '+' ? 1 : -1
      const hours = parseInt(match[2], 10)
      const offsetHours = sign * hours;

      return offsetHours;
    }

    return 0;
  }

  function setSelectedNearbyTideSpot(tideSpotId, saveTideSpot = true) {
    if (selectedNearbyTideSpot.value && tideSpotId === selectedNearbyTideSpot.value?.id) return

    const tideSpot = tideSpotId ? getTideSpotById(tideSpotId) : null;
    
    selectedNearbyTideSpot.value = tideSpot || nearbyTideSpots.value.find(spot => spot.type === 'tide') || nearbyTideSpots.value[0] || null;

    if (saveTideSpot && tideSpot) saveLastSelectedTideSpot(selectedNearbyTideSpot.value.id);

    allData.value = [];
  }

  function saveLastSelectedTideSpot(tideSpotId) {
    savedSelectedTideSpot.value[spotStore.spotId] = tideSpotId;
  }

  function transformTideEvents(tideEvents) {
    let nextEventFlagged = false;

    for (const date in tideEvents) {
      if (!Object.prototype.hasOwnProperty.call(tideEvents, date)) continue;
      
      tideEvents[date] = tideEvents[date].map((event, i) => {
        const formattedTime = `${format(new TZDate(event.dateTime, event.timezoneOffset), 'h:mm a')}`

        event.iconName = event.eventName.toLowerCase().replaceAll(',', '').replaceAll(' ', '-');
        event.timeFormatted = `${formattedTime}`;
        event.timeFormattedWithTimezone = `${formattedTime} <span>${spotStore.localTimezoneShort}</span>`;
        event.isNext = false;
        event.isPrev = false;
        event.isPast = false;
        event.isFuture = false;
        event.type = selectedNearbyTideSpot.value?.type || 'tide';

        if (event.dateTime > Date.now()) {
          event.isFuture = true

          if (!nextEventFlagged) {
            event.isNext = true
            nextEventFlagged = true

            if (tideEvents[i - 1]) tideEvents[i - 1].isPrev = true
          }
        } else {
          event.isPast = true
        }

        return event;
      });
    }

    return tideEvents;
  }

  function getTideSpotById(id) {
    return nearbyTideSpots.value.find(spot => spot.id === id);
  }

  function calculateNowTideValues (data) {
    let [prevIndex, nextIndex, dataDate] = getPrevAndNextIndexes(data);

    if (prevIndex === null || nextIndex === null || prevIndex === -1 || nextIndex === -1) return [null, null, data];
    
    const todayData = data[dataDate];
    const now = Date.now();
    const nowBeforeTimeDelta = now - todayData[prevIndex][0]
    const currentTimePeriodLength = todayData[nextIndex][0] - todayData[prevIndex][0]
    const currentTimePeriodCompletionPercentage = nowBeforeTimeDelta / currentTimePeriodLength
    const interpolatedYValue = parseFloat(((todayData[nextIndex][1] - todayData[prevIndex][1]) * currentTimePeriodCompletionPercentage + todayData[prevIndex][1]).toFixed(2))
    const status = todayData[prevIndex][1] < todayData[nextIndex][1] ? 'rising' : 'falling'
    const timezoneOffset = todayData[prevIndex][2]

    todayData.splice(prevIndex + 1, 0, [now, interpolatedYValue, timezoneOffset])

    return [now, interpolatedYValue, status];
  }

  function getPrevAndNextIndexes(data) {
    if (!data) return [null, null];

    const now = Date.now();
    let nextIndex = null;
    let prevIndex = null;
    let dataDate = null;

    for (let date in data) {
      if (!Object.prototype.hasOwnProperty.call(data, date)) continue;
      
      const foundNextIndex = data[date].findIndex((element) => element[0] > now);

      for (let i = 0; i < data[date].length; i++) {
        if (i === 0 && data[date][i][0] > now) {
          prevIndex = -1
          break;
        };

        if (data[date][i][0] < now) prevIndex = i
      }

      if (foundNextIndex !== -1){
        nextIndex = foundNextIndex;
        dataDate = date;
        break;
      };

    }

    return [prevIndex, nextIndex, dataDate]
  }

  function getDateRangeSolunarEvents(startDate, endDate) {
    if (!startDate || !endDate || !allData.value.length) return [];

    const dateRangeData = allData.value.filter(dataSet => {
      if (!dataSet.startDate === startDate && !dataSet.endDate === endDate) return false;

      return true;
    });

    const combinedSolunarEvents = [];

    if (!dateRangeData.length) return [];

    Object.values(dateRangeData[0].solunarEvents.data).forEach(events => combinedSolunarEvents.push(...events));

    return combinedSolunarEvents;
  }

  function getDataSetForDate(data, date) {
    if (!data || !date) return;

    for (let datasetDate in data) {
      if (!Object.prototype.hasOwnProperty.call(data, datasetDate)) continue;

      const dateSetData = data[datasetDate];
      const datasetDateString = datasetDate.split('T')[0];
      const tideDateDateStr = format(date, 'yyyy-MM-dd');

      if (datasetDateString === tideDateDateStr) return dateSetData;
    }

    return;
  }

  function allDataCleanup() {
    // Keep only the data set that includes the currently selected date
    allData.value = [dateRangeDataSetContaingSelectedDate.value];
  }

  function loadInitialTideSpot() {
    // There being no tide spot selected means the tide container just loaded and a spot needs to be selected
    // either a saved one (if it exists) or the default one.
    if (nearbyTideSpots.value.length && !selectedNearbyTideSpot.value) {
      if (
        Object.keys(savedSelectedTideSpot.value).length &&
        savedSelectedTideSpot.value[`${spotStore.spotId}`]
      ) {
        setSelectedNearbyTideSpot(savedSelectedTideSpot.value[`${spotStore.spotId}`], false)
        
        return
      }
      
      setSelectedNearbyTideSpot(null, false)
    }
  }

  function getSettings() {
    if (!selectedNearbyTideSpot.value) return {};

    return {
      spotId: spotStore.spotId,
      type: selectedNearbyTideSpot.value ? 'tide' : null,
      tideSpotName: selectedNearbyTideSpot.value ? selectedNearbyTideSpot.value.name : null,
      tideSpotSearch: selectedNearbyTideSpot.value ? `${selectedNearbyTideSpot.value.lat}:${selectedNearbyTideSpot.value.lon}` : null
    }
  }

  watchEffect(() => {
    savedTideSpotTimeout.value = setTimeout(() => {
      loadInitialTideSpot();
    }, 5000);

    if (!savedSelectedTideSpotLoaded.value || selectedNearbyTideSpot.value) return;

    if (savedTideSpotTimeout.value) {
      clearTimeout(savedTideSpotTimeout.value);
      savedTideSpotTimeout.value = null;
    }

    loadInitialTideSpot();
  });

  return {
    allData,
    hasCurrentsSpot,
    moonrise,
    moonset,
    moonStatus,
    nearbyTideSpots,
    nextTideEvent,
    nowTideValues,
    selectedNearbyTideSpot,
    dateRangeDataSetContaingSelectedDate,
    solunarEvents,
    sunrise,
    sunset,
    tideData,
    tideDataMin,
    tideDataMax,
    tideDate,
    tideEvents,
    tideSpotTimezone,
    tideSpotTimezoneOffset,
    allDataCleanup,
    getDateRangeSolunarEvents,
    getNearbyTideSpotsByLatLon,
    getAllTideDataByDateRange,
    getTideData,
    getTideEvents,
    setSelectedNearbyTideSpot,
    getSettings
  }
})
